import { useState } from "react";

export default function DemoStory() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("");

  const failureJson = {
    confidence: 60,
    narrative:
      "The organization has satisfied the requirement for conducting an access review, as evidenced by the completion date of March 15. However, there is insufficient evidence to confirm that authorized users are clearly identified and validated, and no evidence confirming that access changes were executed.",
    evidence_mapping: [
      {
        requirement: "Access review conducted",
        evidence: "Access review completed on March 15.",
        status: "Satisfied",
      },
      {
        requirement: "Authorized users identified",
        evidence: "User: John (Admin), User: Mary (User)",
        status: "Partial",
      },
      {
        requirement: "Access changes executed",
        evidence: "Please remove John's admin access.",
        status: "Missing",
      },
    ],
    gaps: [
      "No validation of authorized users.",
      "No confirmation that access changes were executed.",
    ],
    actions: [
      "Provide validation for authorized users.",
      "Document confirmation of access changes.",
    ],
  };

  const successJson = {
    confidence: 100,
    narrative:
      "An access review was conducted with clear identification and validation of authorized users. Required access changes were executed and documented with traceable evidence. The control is fully satisfied.",
    evidence_mapping: [
      {
        requirement: "Access review conducted",
        evidence: "Quarterly Access Review – March 15, 2026",
        status: "Satisfied",
      },
      {
        requirement: "Authorized users identified",
        evidence:
          "Users validated against HR roster: John (Admin), Mary (User)",
        status: "Satisfied",
      },
      {
        requirement: "Access changes executed",
        evidence:
          "Admin privileges revoked and logged (Ticket IT-AR-2026-0315)",
        status: "Satisfied",
      },
    ],
    gaps: [],
    actions: [],
  };

  const runDemo = (data) => {
    setLoading(true);
    setResult(null);

    setStep("Analyzing access review records...");

    setTimeout(() => {
      setStep("Identifying authorized users...");
    }, 800);

    setTimeout(() => {
      setStep("Checking access change execution...");
    }, 1600);

    setTimeout(() => {
      setStep("Generating audit assessment...");
    }, 2400);

    setTimeout(() => {
      setResult(data);
      setLoading(false);
      setStep("");
    }, 3200);
  };

  const allSatisfied =
    result?.evidence_mapping?.every(
      (item) => item.status === "Satisfied"
    ) ?? false;

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex justify-center">
      <div className="w-full max-w-4xl space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold">
            Fluxonix Audit Generator
          </h1>
          <p className="text-gray-600">
            Controlled Demo – Access Control (AC.L2-3.1.1)
          </p>
        </div>

        {/* Buttons ALWAYS visible */}
        <div className="flex gap-4">
          <button
            onClick={() => runDemo(failureJson)}
            className="bg-red-600 text-white px-4 py-2 rounded"
          >
            Load Failed Case
          </button>

          <button
            onClick={() => runDemo(successJson)}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Load Successful Case
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              <p className="text-lg font-medium">{step}</p>
            </div>
          </div>
        )}

        {/* Result */}
        {!loading && result && (
          <div className="bg-white rounded-xl shadow p-6 space-y-6">

            {/* Header */}
            <div>
              <h2 className="text-xl font-semibold">
                AC.L2-3.1.1 — Access Control
              </h2>
              <p className="text-gray-500">Authorized Access</p>
            </div>

            {/* Confidence */}
            <div className="text-3xl font-bold text-blue-600">
              {result.confidence}% Confidence
            </div>

            {/* Conclusion */}
            <div
              className={`text-lg font-semibold ${
                allSatisfied ? "text-green-600" : "text-red-600"
              }`}
            >
              Audit Conclusion:{" "}
              {allSatisfied ? "Fully Satisfied" : "Not Fully Satisfied"}
            </div>

            {/* Narrative */}
            <div>
              <h3 className="font-semibold mb-2">Assessment Summary</h3>
              <p className="text-gray-700">{result.narrative}</p>
            </div>

            {/* Gaps */}
            {!allSatisfied && result.gaps.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-700 mb-2">
                  ❗ Critical Gaps
                </h3>
                <ul className="list-disc pl-5">
                  {result.gaps.map((gap, idx) => (
                    <li key={idx}>{gap}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Insight */}
            {!allSatisfied ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p>
                  <strong>Insight:</strong> Access review was performed, but
                  there is no evidence that decisions were executed.
                </p>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p>
                  <strong>Insight:</strong> Access review, validation, and
                  execution are fully documented.
                </p>
              </div>
            )}

            {/* Table */}
            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Requirement</th>
                  <th className="p-2 text-left">Evidence</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {result.evidence_mapping.map((row, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="p-2">{row.requirement}</td>
                    <td className="p-2">{row.evidence}</td>
                    <td className="p-2">
                      {row.status === "Satisfied" && "✅"}
                      {row.status === "Partial" && "⚠️"}
                      {row.status === "Missing" && "❌"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

          </div>
        )}
      </div>
    </div>
  );
}